import React from 'react';
import { Button } from '@chakra-ui/react';

import {
  Modal,
  InputWithConfirm,
  CurrencyInputWithConfirm,
  RadioOptionsWithConfirm,
  PercentageInputWithConfirm,
  IQuestion,
} from 'shared-components';

import { ScholarshipSponsorship, InputFieldConfig, FundingSummary } from '../../../../types';
import { StudentState } from '../../../../store/student/types';
import FundingCoverSelect from '../../../atoms/funding-cover-select';
import { getPageStatus } from './utils';
import { coversCourses, coversLiving } from '../../../../utilities/sponsors';
import { t } from "i18next";

interface IProps {
  sponsorKey: string | null;
  id: string;
  fundingSummary: FundingSummary;
  saveButtonText: string;
  config: StudentState['config'];
  onSave: (data: ScholarshipSponsorship) => void;
  onClose: () => void;
}

type IComponentProps = Partial<ScholarshipSponsorship> & IProps;

interface IState {
  scholarshipName: string;
  sponsorshipCovers: Array<string>;
  courseFundType: string;
  courseFundPercentage: number;
  courseFundAmount: number;
  livingFundType: string;
  livingFundPercentage: number;
  livingFundAmountType: string;
  livingFundAmount: number;
  courseAndLivingFundSeparate?: boolean;
  courseAndLivingFundType: string;
  courseAndLivingFundPercentage: number;
  courseAndLivingFundAmount: number;

  scholarshipNameError: string;
  coursePercentageError: string;
  courseAmountError: string;
  livingPercentageError: string;
  livingAmountError: string;
  courseAndLivingPercentageError: string;
  courseAndLivingAmountError: string;
}

class ScholarshipSponsor extends React.Component<IComponentProps, IState> {
  constructor(props: IComponentProps) {
    super(props);
    this.state = {
      scholarshipName: props.scholarshipName || '',
      sponsorshipCovers: props.sponsorshipCovers || [],
      courseFundType: props.courseFundType || '',
      courseFundPercentage: props.courseFundPercentage || 0,
      courseFundAmount: props.courseFundAmount || 0,
      livingFundType: props.livingFundType || '',
      livingFundPercentage: props.livingFundPercentage || 0,
      livingFundAmountType: props.livingFundAmountType || '',
      livingFundAmount: props.livingFundAmount || 0,
      courseAndLivingFundSeparate: props.courseAndLivingFundSeparate,
      courseAndLivingFundType: props.courseAndLivingFundType || '',
      courseAndLivingFundPercentage: props.courseAndLivingFundPercentage || 0,
      courseAndLivingFundAmount: props.courseAndLivingFundAmount || 0,

      scholarshipNameError: '',
      coursePercentageError: '',
      courseAmountError: '',
      livingPercentageError: '',
      livingAmountError: '',
      courseAndLivingPercentageError: '',
      courseAndLivingAmountError: '',
    };
  }

  componentDidUpdate(_prevProps: IComponentProps, prevState: IState) {
    if (
      coversCourses(prevState.sponsorshipCovers) !== coversCourses(this.state.sponsorshipCovers) ||
      coversLiving(prevState.sponsorshipCovers) !== coversLiving(this.state.sponsorshipCovers)
    ) {
      this.setState({
        courseFundType: '',
        courseFundPercentage: 0,
        courseFundAmount: 0,
        livingFundType: '',
        livingFundPercentage: 0,
        livingFundAmountType: '',
        livingFundAmount: 0,
        courseAndLivingFundSeparate: undefined,
        courseAndLivingFundType: '',
        courseAndLivingFundPercentage: 0,
        courseAndLivingFundAmount: 0,
      });
    }

    if (prevState.courseAndLivingFundSeparate !== this.state.courseAndLivingFundSeparate) {
      this.setState({
        courseFundType: '',
        courseFundPercentage: 0,
        courseFundAmount: 0,
        livingFundType: '',
        livingFundPercentage: 0,
        livingFundAmountType: '',
        livingFundAmount: 0,
        courseAndLivingFundType: '',
        courseAndLivingFundPercentage: 0,
        courseAndLivingFundAmount: 0,
      });
    }

    if (prevState.courseFundType !== this.state.courseFundType) {
      this.setState({
        courseFundPercentage: 0,
        courseFundAmount: 0,
      });
    }

    if (prevState.livingFundType !== this.state.livingFundType) {
      this.setState({
        livingFundPercentage: 0,
        livingFundAmountType: '',
        livingFundAmount: 0,
      });
    }

    if (prevState.livingFundAmountType !== this.state.livingFundAmountType) {
      this.setState({
        livingFundAmount: 0,
      });
    }

    if (prevState.courseAndLivingFundType !== this.state.courseAndLivingFundType) {
      this.setState({
        courseAndLivingFundPercentage: 0,
        courseAndLivingFundAmount: 0,
      });
    }
  }

  onScholarshipNameChange = (event: React.FormEvent<HTMLInputElement>) => {
    this.setState({
      scholarshipName: event.currentTarget.value,
    });
  };

  getPageStatus = () => {
    return getPageStatus({
      scholarshipName: this.state.scholarshipName,
      sponsorshipCovers: this.state.sponsorshipCovers,
      courseFundType: this.state.courseFundType,
      courseFundPercentage: this.state.courseFundPercentage,
      courseFundAmount: this.state.courseFundAmount,
      livingFundType: this.state.livingFundType,
      livingFundPercentage: this.state.livingFundPercentage,
      livingFundAmountType: this.state.livingFundAmountType,
      livingFundAmount: this.state.livingFundAmount,
      courseAndLivingFundSeparate: this.state.courseAndLivingFundSeparate,
      courseAndLivingFundType: this.state.courseAndLivingFundType,
      courseAndLivingFundPercentage: this.state.courseAndLivingFundPercentage,
      courseAndLivingFundAmount: this.state.courseAndLivingFundAmount,
    });
  };

  onSaveRegionDetails = () => {
    this.props.onSave({
      scholarshipName: this.state.scholarshipName,
      sponsorshipCovers: this.state.sponsorshipCovers,
      courseFundType: this.state.courseFundType as ScholarshipSponsorship['courseFundType'],
      courseFundPercentage: this.state.courseFundPercentage,
      courseFundAmount: this.state.courseFundAmount,
      livingFundType: this.state.livingFundType as ScholarshipSponsorship['livingFundType'],
      livingFundPercentage: this.state.livingFundPercentage,
      livingFundAmountType: this.state.livingFundAmountType as ScholarshipSponsorship['livingFundAmountType'],
      livingFundAmount: this.state.livingFundAmount,
      courseAndLivingFundSeparate: this.state.courseAndLivingFundSeparate,
      courseAndLivingFundType: this.state.courseAndLivingFundType as ScholarshipSponsorship['courseAndLivingFundType'],
      courseAndLivingFundPercentage: this.state.courseAndLivingFundPercentage,
      courseAndLivingFundAmount: this.state.courseAndLivingFundAmount,
    });
    this.props.onClose();
  };

  onCourseTypeChange = (question: IQuestion) => {
    let value = '';
    switch (question.label) {
      case 'The full amount of the above costs':
        value = 'Full';
        break;
      case 'A part percentage of the above costs':
        value = 'Percentage';
        break;
      case 'A specific amount':
        value = 'Amount';
        break;
      default:
        value = '';
        break;
    }
    this.setState({
      courseFundType: value,
    });
  };

  onLivingTypeChange = (question: IQuestion) => {
    let value = '';
    switch (question.label) {
      case 'The full amount of the above costs':
        value = 'Full';
        break;
      case 'A part percentage of the above costs':
        value = 'Percentage';
        break;
      case 'A specific amount':
        value = 'Amount';
        break;
      default:
        value = '';
        break;
    }
    this.setState({
      livingFundType: value,
    });
  };

  onLivingAmountTypeChange = (question: IQuestion) => {
    let value = '';
    switch (question.label) {
      case 'Monthly Stipend':
        value = 'Monthly';
        break;
      case 'Upfront':
        value = 'Total';
        break;
      default:
        value = '';
        break;
    }
    this.setState({
      livingFundAmountType: value,
    });
  };

  onCourseAndLivingTypeChange = (question: IQuestion) => {
    let value = '';
    switch (question.label) {
      case 'The full amount of the above costs':
        value = 'Full';
        break;
      case 'A part percentage of the above costs':
        value = 'Percentage';
        break;
      case 'A specific amount':
        value = 'Amount';
        break;
      default:
        value = '';
        break;
    }
    this.setState({
      courseAndLivingFundType: value,
    });
  };

  renderCourse() {
    const {
      CoverType: coverTypeConfig,
    }: {
      CoverType: InputFieldConfig;
    } = this.props.config.Checklist.Finances.Page1;

    return (
      <>
        <RadioOptionsWithConfirm
          id="courseType"
          label={t('common:howMuchOfTheTuitionFeesDoesTheSc')}
          questions={[
            {
              char: '1',
              label: 'The full amount of the above costs',
              selected: this.state.courseFundType === 'Full',
            },
            {
              char: '2',
              label: 'A part percentage of the above costs',
              selected: this.state.courseFundType === 'Percentage',
            },
            {
              char: '3',
              label: 'A specific amount',
              selected: this.state.courseFundType === 'Amount',
            },
          ]}
          inputHelperText={coverTypeConfig?.helpText}
          onClick={this.onCourseTypeChange}
        />

        {this.state.courseFundType === 'Percentage' ? (
          <PercentageInputWithConfirm
            id="coursePercentage"
            label={t('common:whatPercentageIsBeingCovered')}
            value={this.state.courseFundPercentage}
            errorText={this.state.coursePercentageError}
            onChange={(event: React.FormEvent<HTMLInputElement>) => {
              const value = Number(event.currentTarget.value) || 0;
              this.setState({
                courseFundPercentage: value,
              });
            }}
            onBlur={(event: React.FormEvent<HTMLInputElement>) => {
              if (event.currentTarget.value === '') {
                this.setState({
                  coursePercentageError: 'Please enter a percentage value',
                });
              }
            }}
            onFocus={() => {
              this.setState({
                coursePercentageError: '',
              });
            }}
          />
        ) : null}

        {this.state.courseFundType === 'Amount' ? (
          <CurrencyInputWithConfirm
            id="courseAmount"
            label={t('common:whatIsTheAmountBeingCoveredInPou')}
            currencySymbol="£"
            value={this.state.courseFundAmount}
            errorText={this.state.courseAmountError}
            onChange={(event: React.FormEvent<HTMLInputElement>) => {
              const value = Number(event.currentTarget.value) || 0;
              this.setState({
                courseFundAmount: value,
              });
            }}
            onBlur={(event: React.FormEvent<HTMLInputElement>) => {
              if (event.currentTarget.value === '') {
                this.setState({
                  courseAmountError: 'Please enter an amount',
                });
              }
            }}
            onFocus={() => {
              this.setState({
                courseAmountError: '',
              });
            }}
          />
        ) : null}
      </>
    );
  }

  renderLiving() {
    const {
      CoverType: coverTypeConfig,
      MonthlyOrUpfrontLivingCost: monthlyOrUpfrontLivingCostConfig,
      TotalLivingCost: totalLivingCostConfig,
      MonthlyLivingCost: monthlyLivingCostConfig,
    }: {
      CoverType: InputFieldConfig;
      MonthlyOrUpfrontLivingCost: InputFieldConfig;
      TotalLivingCost: InputFieldConfig;
      MonthlyLivingCost: InputFieldConfig;
    } = this.props.config.Checklist.Finances.Page1;

    return (
      <>
        <RadioOptionsWithConfirm
          id="livingType"
          label={t('common:howMuchOfYourLivingCostsDoesThis2')}
          questions={[
            {
              char: '1',
              label: 'The full amount of the above costs',
              selected: this.state.livingFundType === 'Full',
            },
            {
              char: '2',
              label: 'A part percentage of the above costs',
              selected: this.state.livingFundType === 'Percentage',
            },
            {
              char: '3',
              label: 'A specific amount',
              selected: this.state.livingFundType === 'Amount',
            },
          ]}
          inputHelperText={coverTypeConfig?.helpText}
          onClick={this.onLivingTypeChange}
        />

        {this.state.livingFundType === 'Percentage' ? (
          <PercentageInputWithConfirm
            id="livingPercentage"
            label={t('common:whatPercentageIsBeingCovered')}
            value={this.state.livingFundPercentage}
            errorText={this.state.livingPercentageError}
            onChange={(event: React.FormEvent<HTMLInputElement>) => {
              const value = Number(event.currentTarget.value) || 0;
              this.setState({
                livingFundPercentage: value,
              });
            }}
            onBlur={(event: React.FormEvent<HTMLInputElement>) => {
              if (event.currentTarget.value === '') {
                this.setState({
                  livingPercentageError: 'Please enter a percentage value',
                });
              }
            }}
            onFocus={() => {
              this.setState({
                livingPercentageError: '',
              });
            }}
          />
        ) : null}

        {this.state.livingFundType === 'Amount' ? (
          <>
            <RadioOptionsWithConfirm
              id="livingAmount"
              label={monthlyOrUpfrontLivingCostConfig?.label}
              questions={[
                {
                  char: '1',
                  label: 'Monthly Stipend',
                  selected: this.state.livingFundAmountType === 'Monthly',
                },
                {
                  char: '2',
                  label: 'Upfront',
                  selected: this.state.livingFundAmountType === 'Total',
                },
              ]}
              inputHelperText={monthlyOrUpfrontLivingCostConfig?.helpText}
              onClick={this.onLivingAmountTypeChange}
            />

            {this.state.livingFundAmountType === 'Monthly' ? (
              <CurrencyInputWithConfirm
                id="livingMonthlyAmount"
                label={monthlyLivingCostConfig?.label}
                currencySymbol="£"
                value={this.state.livingFundAmount}
                errorText={this.state.livingAmountError}
                onChange={(event: React.FormEvent<HTMLInputElement>) => {
                  const value = Number(event.currentTarget.value) || 0;
                  this.setState({
                    livingFundAmount: value,
                  });
                }}
                onFocus={() => {
                  this.setState({
                    livingAmountError: '',
                  });
                }}
                onBlur={(event: React.FormEvent<HTMLInputElement>) => {
                  if (event.currentTarget.value === '') {
                    this.setState({
                      livingAmountError: 'Please enter an amount',
                    });
                  }
                }}
                inputHelperText={monthlyLivingCostConfig?.helpText}
              />
            ) : null}

            {this.state.livingFundAmountType === 'Total' ? (
              <CurrencyInputWithConfirm
                id="livingTotalAmount"
                label={totalLivingCostConfig?.label}
                currencySymbol="£"
                value={this.state.livingFundAmount}
                errorText={this.state.livingAmountError}
                onChange={(event: React.FormEvent<HTMLInputElement>) => {
                  const value = Number(event.currentTarget.value) || 0;
                  this.setState({
                    livingFundAmount: value,
                  });
                }}
                onFocus={() => {
                  this.setState({
                    livingAmountError: '',
                  });
                }}
                onBlur={(event: React.FormEvent<HTMLInputElement>) => {
                  if (event.currentTarget.value === '') {
                    this.setState({
                      livingAmountError: 'Please enter an amount',
                    });
                  }
                }}
                inputHelperText={totalLivingCostConfig?.helpText}
              />
            ) : null}
          </>
        ) : null}
      </>
    );
  }

  renderCourseAndLiving() {
    const {
      CoverType: coverTypeConfig,
    }: {
      CoverType: InputFieldConfig;
    } = this.props.config.Checklist.Finances.Page1;

    return (
      <>
        <RadioOptionsWithConfirm
          id="courseAndLivingType"
          label={t('common:howMuchOfYourTuitionFeesAndLivin2')}
          questions={[
            {
              char: '1',
              label: 'The full amount of the above costs',
              selected: this.state.courseAndLivingFundType === 'Full',
            },
            {
              char: '2',
              label: 'A part percentage of the above costs',
              selected: this.state.courseAndLivingFundType === 'Percentage',
            },
            {
              char: '3',
              label: 'A specific amount',
              selected: this.state.courseAndLivingFundType === 'Amount',
            },
          ]}
          inputHelperText={coverTypeConfig?.helpText}
          onClick={this.onCourseAndLivingTypeChange}
        />

        {this.state.courseAndLivingFundType === 'Percentage' ? (
          <PercentageInputWithConfirm
            id="courseAndLivingPercentage"
            label={t('common:whatPercentageIsBeingCovered')}
            value={this.state.courseAndLivingFundPercentage}
            errorText={this.state.courseAndLivingPercentageError}
            onChange={(event: React.FormEvent<HTMLInputElement>) => {
              const value = Number(event.currentTarget.value) || 0;
              this.setState({
                courseAndLivingFundPercentage: value,
              });
            }}
            onBlur={(event: React.FormEvent<HTMLInputElement>) => {
              if (event.currentTarget.value === '') {
                this.setState({
                  courseAndLivingPercentageError: 'Please enter a percentage value',
                });
              }
            }}
            onFocus={() => {
              this.setState({
                courseAndLivingPercentageError: '',
              });
            }}
          />
        ) : null}

        {this.state.courseAndLivingFundType === 'Amount' ? (
          <CurrencyInputWithConfirm
            id="courseAndLivingAmount"
            label={t('common:whatIsTheAmountBeingCoveredInPou')}
            currencySymbol="£"
            value={this.state.courseAndLivingFundAmount}
            errorText={this.state.courseAndLivingAmountError}
            onChange={(event: React.FormEvent<HTMLInputElement>) => {
              const value = Number(event.currentTarget.value) || 0;
              this.setState({
                courseAndLivingFundAmount: value,
              });
            }}
            onBlur={(event: React.FormEvent<HTMLInputElement>) => {
              if (event.currentTarget.value === '') {
                this.setState({
                  courseAndLivingAmountError: 'Please enter an amount',
                });
              }
            }}
            onFocus={() => {
              this.setState({
                courseAndLivingAmountError: '',
              });
            }}
            inputHelperText={this.props.config.Checklist.Finances.Page1.AmountCoveredBankSponsor?.helpText}
          />
        ) : null}
      </>
    );
  }

  render() {
    const { complete, visible } = this.getPageStatus();

    const {
      HasSeparateLivingCost: hasSeparateLivingCostConfig,
    }: {
      HasSeparateLivingCost: InputFieldConfig;
    } = this.props.config.Checklist.Finances.Page1;

    return (
      <Modal
        id={this.props.id}
        title={t('common:provideDetailsForYourScholarship')}
        show={true}
        onClose={this.props.onClose}
        footer={
          <Button
            id={'saveButton'}
            onClick={this.onSaveRegionDetails}
            isDisabled={!complete}
            variant={'solid'}
            size={'lg'}
          >
            {this.props.saveButtonText}
          </Button>
        }
      >
        {visible.scholarshipName && (
          <InputWithConfirm
            id="scholarship-sponsor-name"
            value={this.state.scholarshipName}
            label={t('common:scholarshipName')}
            inputHelperText={this.props.config.Checklist.Finances.Page1.ScholarshipName?.helpText}
            errorText={this.state.scholarshipNameError}
            onChange={this.onScholarshipNameChange}
            onBlur={(event: React.FormEvent<HTMLInputElement>) => {
              if (event.currentTarget.value === '') {
                this.setState({
                  scholarshipNameError: 'Please enter the scholarship name',
                });
              }
            }}
            onFocus={() => {
              this.setState({
                scholarshipNameError: '',
              });
            }}
          />
        )}

        {visible.fundingCover && (
          <FundingCoverSelect
            label={t('common:selectTheCostsThatYourScholarshi')}
            subLabel={t('common:yourScholarshipDetailsShouldSpec')}
            fundingSummary={this.props.fundingSummary}
            sponsorshipCovers={this.state.sponsorshipCovers}
            onChange={(sponsorshipCovers: Array<string>) => {
              this.setState({ sponsorshipCovers });
            }}
          />
        )}

        {visible.courseForm && <>{this.renderCourse()}</>}

        {visible.livingForm && <>{this.renderLiving()}</>}

        {visible.courseAndLivingForm && (
          <>
            <RadioOptionsWithConfirm
              id="courseAndLivingFundSeparate"
              label={
                hasSeparateLivingCostConfig?.label &&
                hasSeparateLivingCostConfig.label.replace(/sponsorship/g, 'scholarship')
              }
              questions={[
                {
                  char: 'Y',
                  label: 'Yes',
                  selected: this.state.courseAndLivingFundSeparate === true,
                },
                {
                  char: 'N',
                  label: 'No',
                  selected: this.state.courseAndLivingFundSeparate === false,
                },
              ]}
              inputHelperText={hasSeparateLivingCostConfig?.helpText}
              onClick={(question: IQuestion) =>
                this.setState({
                  courseAndLivingFundSeparate: question.label === 'Yes',
                })
              }
            />
            {this.state.courseAndLivingFundSeparate === true && (
              <>
                {this.renderLiving()}
                {this.renderCourse()}
              </>
            )}
            {this.state.courseAndLivingFundSeparate === false && <>{this.renderCourseAndLiving()}</>}
          </>
        )}
      </Modal>
    );
  }
}

export default ScholarshipSponsor;
